import { ChangeEvent, FormEvent, useState } from 'react';

import { useGeoValue } from '@entities/geo';
import { TOrder, useApproveOrderARSMutation, useApproveOrderMutation } from '@entities/orders';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { FormItemWithError } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';
import { trimDecimalPart } from '@shared/utils/formatAmount';

import { IOrderModalContextProps } from '../model/types';

interface IProps<T> {
  order: T;
  refetch: () => void;
}

const validateUTR = (value: string): boolean => /^(\d{12})(\**)$/.test(value); // 12 digits, optionally followed by asterisks (*)
const validateCuitCuil = (value: string): boolean => /^(\d{2}-\d{8}-\d|\d{11})$/.test(value); // XX-XXXXXXXX-X or XXXXXXXXXXX (11 digits, with or without dashes)
const validateCoelsaId = (value: string): boolean => /^[A-Za-z0-9]{24}$/.test(value); // Latin alphanumeric, exactly 24 chars

const VALIDATION_ERRORS = {
  customer_transaction_id: 'UTR must be exactly 12 digits, optionally followed by asterisks (*)',
  cuit_cuil: 'Cuit/Cuil must be XX-XXXXXXXX-X or 11 digits',
  coelsa_id: 'Coelsa ID must be exactly 24 alphanumeric characters',
  at_least_one_required: 'At least one of Cuit/Cuil or Coelsa ID must be filled',
} as const;

const createValidationError = (
  messageKey: keyof typeof VALIDATION_ERRORS,
  displayField: string = messageKey,
): FetchBaseQueryError => ({
  status: 422,
  data: { errors: { [displayField]: [VALIDATION_ERRORS[messageKey]] } },
});

const computeArsErrors = (cuitCuil: string, coelsaId: string) => {
  if (!cuitCuil && !coelsaId) {
    return {
      cuitCuil: createValidationError('at_least_one_required', 'cuit_cuil'),
      coelsaId: createValidationError('at_least_one_required', 'coelsa_id'),
    };
  }

  return {
    cuitCuil: cuitCuil && !validateCuitCuil(cuitCuil) ? createValidationError('cuit_cuil') : null,
    coelsaId: coelsaId && !validateCoelsaId(coelsaId) ? createValidationError('coelsa_id') : null,
  };
};
export const ApproveOrder = <T extends TOrder>({ order, refetch }: IProps<T>) => {
  const { requested_amount, actual_amount, id, customer_transaction_id, status_reason } = order;

  const [actualAmount, setActualAmount] = useState(actual_amount || requested_amount);
  const [customerTransactionId, setCustomerTransactionId] = useState(customer_transaction_id || '');
  const [reason, setReason] = useState(status_reason);
  const [cuitCuil, setCuitCuil] = useState('');
  const [coelsaId, setCoelsaId] = useState('');
  const [utrError, setUtrError] = useState<FetchBaseQueryError | null>(null);
  const [cuitCuilError, setCuitCuilError] = useState<FetchBaseQueryError | null>(null);
  const [coelsaIdError, setCoelsaIdError] = useState<FetchBaseQueryError | null>(null);

  const geo = useGeoValue();
  const isArs = geo === 'ars';

  const [approveOrder, { isLoading, isError, error, isSuccess }] = useApproveOrderMutation();
  const [approveOrderARS, { isLoading: isLoadingARS, isError: isErrorARS, error: errorARS, isSuccess: isSuccessARS }] = useApproveOrderARSMutation();

  const currentIsLoading = isArs ? isLoadingARS : isLoading;
  const currentIsError = isArs ? isErrorARS : isError;
  const currentError = isArs ? errorARS : error;
  const currentIsSuccess = isArs ? isSuccessARS : isSuccess;

  const {
    closeModal,
    additionalContext: { setSelectedIds },
  } = useModal<IOrderModalContextProps>();

  const handleChangeCustomerTransactionId = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerTransactionId(value);

    if (value && !validateUTR(value)) {
      setUtrError(createValidationError('customer_transaction_id'));
    } else {
      setUtrError(null);
    }
  };

  const handleChangeCuitCuil = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCuitCuil(value);
    setCuitCuilError(value && !validateCuitCuil(value) ? createValidationError('cuit_cuil') : null);
    if (value && !coelsaId) setCoelsaIdError(null);
  };

  const handleChangeCoelsaId = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCoelsaId(value);
    setCoelsaIdError(value && !validateCoelsaId(value) ? createValidationError('coelsa_id') : null);
    if (value && !cuitCuil) setCuitCuilError(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isArs) {
      const utrError = customerTransactionId && !validateUTR(customerTransactionId)
        ? createValidationError('customer_transaction_id')
        : null;
      setUtrError(utrError);
      if (utrError) return;
    } else {
      const errors = computeArsErrors(cuitCuil, coelsaId);
      setCuitCuilError(errors.cuitCuil);
      setCoelsaIdError(errors.coelsaId);
      if (errors.cuitCuil || errors.coelsaId) return;
    }

    try {
      const result = isArs
        ? await approveOrderARS({ id, actual_amount: actualAmount, cuit_cuil: cuitCuil || null, coelsa_id: coelsaId || null, reason })
        : await approveOrder({ id, actual_amount: actualAmount, customer_transaction_id: customerTransactionId, reason });

      if ('data' in result) {
        setSelectedIds([]);
        closeModal();
        refetch();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <h2>Approve Order</h2>

      <form className='popup-form' onSubmit={handleSubmit}>
        <FormItemWithError
          name='actual_amount'
          label='Actual Amount'
          isError={currentIsError}
          error={currentError}
          secondName='order'
        >
          <input
            type='text'
            name='actual_amount'
            id='actual_amount'
            defaultValue={`${trimDecimalPart(actualAmount)}`}
            onChange={e => setActualAmount(e.target.value)}
          />
        </FormItemWithError>

        {!isArs && (
          <FormItemWithError
            name='customer_transaction_id'
            label='UTR'
            isError={Boolean(utrError) || currentIsError}
            error={utrError || currentError}
            secondName='order'
          >
            <input
              type='text'
              name='customer_transaction_id'
              id='customer_transaction_id'
              defaultValue={customerTransactionId}
              onChange={handleChangeCustomerTransactionId}
            />
          </FormItemWithError>
        )}

        {isArs && (
          <>
            <FormItemWithError
              name='cuit_cuil'
              label='Cuit/Cuil'
              isError={Boolean(cuitCuilError) || currentIsError}
              error={cuitCuilError || currentError}
              secondName='order'
            >
              <input
                type='text'
                name='cuit_cuil'
                id='cuit_cuil'
                value={cuitCuil}
                onChange={handleChangeCuitCuil}
                placeholder='XX-XXXXXXXX-X or 11 digits'
              />
            </FormItemWithError>

            <FormItemWithError
              name='coelsa_id'
              label='Coelsa ID'
              isError={Boolean(coelsaIdError) || currentIsError}
              error={coelsaIdError || currentError}
              secondName='order'
            >
              <input
                type='text'
                name='coelsa_id'
                id='coelsa_id'
                value={coelsaId}
                onChange={handleChangeCoelsaId}
                placeholder='24 alphanumeric characters'
              />
            </FormItemWithError>
          </>
        )}

        <FormItemWithError
          secondName='order'
          name='reason'
          label='Reason'
          isError={currentIsError}
          error={currentError}
        >
          <input
            type='text'
            name='reason'
            id='reason'
            defaultValue={reason}
            onChange={e => setReason(e.target.value)}
          />
        </FormItemWithError>

        {currentIsSuccess && <div className='form-success'>Successfully approved</div>}

        <div className='form-submit'>
          <button type='submit' disabled={currentIsLoading}>
            Approve
          </button>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
};
